import React from 'react';
import Button from '../../../components/ui/Button';

const PermissionChangeModal = ({
  isOpen,
  onClose,
  onConfirm,
  currentRole,
  newRole,
  changes,
  userName
}) => {
  if (!isOpen) return null;

  const roleLabels = {
    admin: 'Administrator',
    manager: 'Manager',
    viewer: 'Viewer'
  };

  const getRoleColor = (role) => {
    const colors = {
      admin: 'text-red-600',
      manager: 'text-orange-600',
      viewer: 'text-green-600'
    };
    return colors?.[role] || 'text-muted-foreground';
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Overlay */}
        <div 
          className="fixed inset-0 transition-opacity bg-black bg-opacity-50"
          onClick={onClose}
        ></div>

        {/* Modal */}
        <div className="inline-block w-full max-w-lg p-6 my-8 text-left align-middle transition-all transform bg-white shadow-xl rounded-lg sm:my-16">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-foreground">
              Confirm Role Change
            </h3>
            <button
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="space-y-4">
            {/* User and Role Change Summary */}
            <div className="bg-muted/20 rounded-lg p-4">
              <p className="text-sm text-foreground mb-2">
                <span className="font-medium">User:</span> {userName}
              </p>
              <div className="flex items-center space-x-3 text-sm">
                <span className="font-medium">Role:</span>
                <span className={getRoleColor(currentRole)}>
                  {roleLabels?.[currentRole]}
                </span>
                <svg className="w-4 h-4 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
                <span className={getRoleColor(newRole)}>
                  {roleLabels?.[newRole]}
                </span>
              </div>
            </div>

            {/* Permission Changes */}
            {(changes?.adding?.length > 0 || changes?.removing?.length > 0) && (
              <div className="space-y-3">
                <h4 className="font-medium text-foreground">Permission Changes</h4>
                
                {/* Adding Permissions */}
                {changes?.adding?.length > 0 && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <h5 className="font-medium text-green-800 mb-2 flex items-center">
                      <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      Adding Permissions
                    </h5>
                    <ul className="space-y-1">
                      {changes?.adding?.map((permission, index) => (
                        <li key={index} className="text-sm text-green-700 flex items-center">
                          <svg className="w-3 h-3 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          {permission}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Removing Permissions */}
                {changes?.removing?.length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <h5 className="font-medium text-red-800 mb-2 flex items-center">
                      <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                      </svg>
                      Removing Permissions
                    </h5>
                    <ul className="space-y-1">
                      {changes?.removing?.map((permission, index) => (
                        <li key={index} className="text-sm text-red-700 flex items-center">
                          <svg className="w-3 h-3 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                          {permission}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Warning */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <div className="flex items-start space-x-2">
                <svg className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <div className="text-sm text-yellow-800">
                  <p className="font-medium mb-1">Important</p>
                  <ul className="space-y-1 text-xs">
                    <li>• This change takes effect immediately</li>
                    <li>• User may need to log in again to see updated permissions</li>
                    <li>• This action will be logged for audit purposes</li>
                    {currentRole === 'admin' && newRole !== 'admin' && (
                      <li className="text-red-600 font-medium">• This will remove administrative privileges</li>
                    )}
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 mt-6">
            <Button
              variant="outline"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              onClick={onConfirm}
              iconName="Check"
              iconPosition="left"
            >
              Confirm Change
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PermissionChangeModal;